// src/components/richtext/MobileFormatSheet.jsx
// Anchored popover on desktop, bottom sheet on mobile.
import React, { useEffect, useLayoutEffect, useState } from 'react'

const MOBILE_BREAKPOINT = 640

export default function MobileFormatSheet({ isOpen, onClose, sections, anchorRef }) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT,
  )
  const [coords, setCoords] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    // Lock body scroll only in bottom-sheet mode — popover should let the page breathe.
    if (isMobile) document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose, isMobile])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Measure the anchor button so the desktop popover lines up under it.
  useLayoutEffect(() => {
    if (!isOpen || isMobile || !anchorRef?.current) {
      setCoords(null)
      return
    }
    const update = () => {
      const rect = anchorRef.current?.getBoundingClientRect()
      if (rect) {
        setCoords({
          top: rect.bottom + 6,
          right: Math.max(8, window.innerWidth - rect.right),
        })
      }
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [isOpen, isMobile, anchorRef])

  if (!isOpen) return null

  // ---------- Desktop popover ----------
  if (!isMobile) {
    return (
      <>
        <div className="rte-popover-scrim" onClick={onClose} role="presentation" />
        <div
          className="rte-popover"
          role="dialog"
          aria-label="Formatting options"
          style={coords ? { top: coords.top, right: coords.right } : { visibility: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          {sections.map((section) => (
            <div key={section.title} className="rte-popover-section">
              <div className="rte-popover-section-title">{section.title}</div>
              <div className="rte-popover-grid">
                {section.items.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    className={`rte-popover-item ${item.active ? 'is-active' : ''}`}
                    aria-pressed={item.active}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { item.onClick(); onClose() }}
                    disabled={item.disabled}
                    title={item.label}
                  >
                    <span className="rte-popover-icon">{item.icon}</span>
                    <span className="rte-popover-label">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </>
    )
  }

  // ---------- Mobile bottom sheet ----------
  return (
    <div className="rte-sheet-backdrop" onClick={onClose} role="presentation">
      <div
        className="rte-sheet"
        role="dialog"
        aria-label="Formatting options"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rte-sheet-grip" />
        {sections.map((section) => (
          <div key={section.title} className="rte-sheet-section">
            <div className="rte-sheet-section-title">{section.title}</div>
            <div className="rte-sheet-grid">
              {section.items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={`rte-sheet-item ${item.active ? 'is-active' : ''}`}
                  aria-pressed={item.active}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { item.onClick(); onClose() }}
                  disabled={item.disabled}
                >
                  <span className="rte-sheet-item-icon">{item.icon}</span>
                  <span className="rte-sheet-item-label">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
